import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { TenantProvider } from './context/TenantContext';
import { Capacitor } from '@capacitor/core';

// Auto-detect native app and redirect to correct entry point
const getNativeRedirect = () => {
  if (!Capacitor.isNativePlatform()) return null;
  // The app ID is set in capacitor.config.json
  // Client: com.ovadispatch.client → /client
  // Driver: com.ovadispatch.driver → /driver
  const path = window.location.pathname;
  if (path === '/' || path === '/index.html') {
    // Check which native app we're running in
    // Capacitor doesn't expose appId directly, so we use a build-time env var
    if (process.env.REACT_APP_NATIVE_APP === 'driver') return '/driver';
    return '/client'; // Default to client
  }
  return null;
};

// Lazy load components for better performance
const App = React.lazy(() => import('./App'));
const DriverApp = React.lazy(() => import('./DriverApp'));
const ClientApp = React.lazy(() => import('./ClientApp'));
const ClientPWA = React.lazy(() => import('./ClientPWA'));
const DriverSignup = React.lazy(() => import('./DriverSignup'));
const ClientSignup = React.lazy(() => import('./ClientSignup'));
const AdminDashboard = React.lazy(() => import('./AdminDashboard'));
const RevolutionaryAdminDashboard = React.lazy(() => import('./RevolutionaryAdminDashboard'));
const BrokerRateOverrides = React.lazy(() => import('./components/admin/BrokerRateOverrides'));
const ClientSuccessLaunchCenter = React.lazy(() => import('./components/admin/ClientSuccessLaunchCenter'));
const UnifiedCommandCenter = React.lazy(() => import('./components/CommandCenterWithAuth'));
const TestDashboard = React.lazy(() => import('./TestDashboard'));
const MinimalDashboard = React.lazy(() => import('./MinimalDashboard'));
const VAApprovalPortal = React.lazy(() => import('./VAApprovalPortal'));
const AILandingPage = React.lazy(() => import('./AILandingPage'));
const ProfessionalLandingPage = React.lazy(() => import('./ProfessionalLandingPage'));
const SaasProvisioningSystem = React.lazy(() => import('./components/SaasProvisioningSystem'));
const SaasBillingSuccess = React.lazy(() => import('./pages/SaasBillingSuccess'));
const SaasBillingCancelled = React.lazy(() => import('./pages/SaasBillingCancelled'));
const MapboxTestPage = React.lazy(() => import('./components/MapboxTestPage'));
const RouteTestPage = React.lazy(() => import('./components/RouteTestPage'));
const TestingAccessPage = React.lazy(() => import('./components/TestingAccessPage'));
const BrokerPortal = React.lazy(() => import('./components/BrokerPortal'));
const PublicTripTracker = React.lazy(() => import('./pages/PublicTripTracker'));
const ReferralPortal = React.lazy(() => import('./pages/ReferralPortal'));
const LegalPages = React.lazy(() => import('./pages/LegalPages'));
const LogoPreview = React.lazy(() => import('./LogoPreview'));
const GiftRideReceiver = React.lazy(() => import('./components/GiftRideReceiver'));
const CarlsonLawPortal = React.lazy(() => import('./components/CarlsonLawPortal'));
const PartnerSignup = React.lazy(() => import('./PartnerSignup'));
const PartnerSignupSuccess = React.lazy(() => import('./PartnerSignupSuccess'));
const FacilitySignupPage = React.lazy(() =>
  import('./FreeSignupForms').then(m => ({ default: m.FacilitySignup }))
);
const CustomPortalRequestPage = React.lazy(() =>
  import('./FreeSignupForms').then(m => ({ default: m.CustomPortalRequest }))
);

// Tenant-aware loading component
const LoadingSpinner = () => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin" 
           style={{ background: 'linear-gradient(135deg, var(--tenant-primary, #22c55e), var(--tenant-secondary, #000000))' }}>
        <span className="text-white font-bold text-xl">N</span>
      </div>
      <p className="text-xl text-gray-300 font-mono">Loading...</p>
    </div>
  </div>
);

// Global error boundary — a crashed route must NEVER show a blank white page.
// Offers a one-click recovery that also clears stale PWA caches (the usual
// culprit after a redeploy) before reloading.
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('AppErrorBoundary caught:', error, info?.componentStack);
  }

  handleReload = async () => {
    try {
      if ('caches' in window) {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((k) => window.caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.update()));
      }
    } catch (_) { /* best effort */ }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6"
             data-testid="app-error-boundary">
          <div className="max-w-md text-center space-y-4">
            <div className="text-5xl">⚠️</div>
            <h1 className="text-2xl font-bold">Something went wrong</h1>
            <p className="text-slate-400 text-sm">
              This page hit an unexpected error. Tap below to refresh — this also
              clears any outdated cached files from a recent update.
            </p>
            <button onClick={this.handleReload}
              className="px-6 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 font-semibold"
              data-testid="error-boundary-reload-btn">
              Refresh App
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


function AppRouter() {
  // Hostname-based white-label routing.
  // When a partner hits their custom domain (e.g. portal.carlsonlaw.com), every
  // route maps to their branded portal — no OVA/Emergent paths visible to clients.
  const host = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
  const isCarlsonHost = host.includes('carlson');

  if (isCarlsonHost) {
    return (
      <AppErrorBoundary>
      <BrowserRouter>
        <TenantProvider>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/t/:tripId" element={<PublicTripTracker />} />
              <Route path="/track/:tripId" element={<PublicTripTracker />} />
              <Route path="/gift/:token" element={<GiftRideReceiver />} />
              <Route path="*" element={<CarlsonLawPortal />} />
            </Routes>
          </Suspense>
        </TenantProvider>
      </BrowserRouter>
      </AppErrorBoundary>
    );
  }

  return (
    <AppErrorBoundary>
    <BrowserRouter>
      <TenantProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Legacy AI Landing Page - old "World's First Autonomous" page */}
            <Route path="/ai" element={<AILandingPage />} />
            {/* NEW Professional Landing Page - "Go Anywhere, Anytime" */}
            <Route path="/onboard" element={<ProfessionalLandingPage />} />
            <Route path="/landing" element={<ProfessionalLandingPage />} />
            
            {/* SaaS Multi-Tenant Provisioning System */}
            <Route path="/saas" element={<SaasProvisioningSystem />} />
            <Route path="/saas/signup" element={<SaasProvisioningSystem />} />
            <Route path="/saas/billing/success" element={<SaasBillingSuccess />} />
            <Route path="/saas/billing/cancelled" element={<SaasBillingCancelled />} />
            <Route path="/signup" element={<SaasProvisioningSystem />} />
            
            {/* Client PWA Routes */}
            <Route path="/client" element={<ClientPWA />} />
            <Route path="/client/app" element={<ClientPWA />} />
            <Route path="/ride" element={<ClientPWA />} />
            <Route path="/rider" element={<ClientPWA />} />
            
            {/* Public Trip Tracking - No login required */}
            <Route path="/t/:tripId" element={<PublicTripTracker />} />
            <Route path="/track/:tripId" element={<PublicTripTracker />} />
            <Route path="/referral-portal" element={<ReferralPortal />} />
            
            {/* Gift Ride - Receiver page (no login required) */}
            <Route path="/gift/:token" element={<GiftRideReceiver />} />
            
            {/* Law Firm / Generic Partner Portal — same login UI for any seeded org */}
            <Route path="/partner/carlson-law" element={<CarlsonLawPortal />} />
            <Route path="/partner/a-stat-transit" element={<CarlsonLawPortal />} />
            <Route path="/portal/login" element={<CarlsonLawPortal />} />

            {/* Public partner self-service signup */}
            <Route path="/partners/signup" element={<PartnerSignup />} />
            <Route path="/partners/signup/success" element={<PartnerSignupSuccess />} />
            <Route path="/fleet/signup" element={
              <PartnerSignup
                lockVertical="fleet"
                headline={<>Run a fleet? <span className="text-amber-400">Plug it into OVA in minutes.</span></>}
                subhead="Manage drivers, dispatch, payouts, and live tracking from one branded portal — Stripe-backed and ready in under 5 minutes."
              />
            } />
            <Route path="/broker/signup" element={
              <PartnerSignup
                lockVertical="broker"
                headline={<>Broker trips. <span className="text-amber-400">At scale.</span></>}
                subhead="Submit, track, reconcile, and EDI-export thousands of broker authorizations through a HIPAA-aware portal."
              />
            } />
            <Route path="/facility/signup" element={<FacilitySignupPage />} />
            <Route path="/portal/request" element={<CustomPortalRequestPage />} />
            
            {/* Legacy Client Routes - Signup forms */}
            <Route path="/client/signup" element={<ClientSignup />} />
            <Route path="/ride/signup" element={<ClientSignup />} />
            <Route path="/client/legacy" element={<ClientApp />} />
            
            {/* Driver Signup & App Routes */}
            <Route path="/driver/signup" element={<DriverSignup />} />
            <Route path="/drive/signup" element={<DriverSignup />} />
            <Route path="/driver" element={<DriverSignup />} />
            <Route path="/driver/app" element={<DriverApp />} />
            
            {/* VA Portal Route */}
            <Route path="/va-portal" element={<VAApprovalPortal />} />
            <Route path="/va-portal/*" element={<VAApprovalPortal />} />
            
            {/* Broker Portal Route */}
            <Route path="/broker" element={<BrokerPortal />} />
            <Route path="/broker-portal" element={<BrokerPortal />} />
            <Route path="/partner" element={<BrokerPortal />} />

            {/* Admin: Broker Rate Overrides */}
            <Route path="/admin/broker-rates" element={<BrokerRateOverrides />} />

            {/* Client Success Journey — Launch Center (Step 2) */}
            <Route path="/portal/launch/:tenantId" element={<ClientSuccessLaunchCenter />} />
            
            {/* Test Routes */}
            <Route path="/test-access" element={<TestingAccessPage />} />
            <Route path="/map-test" element={<MapboxTestPage />} />
            <Route path="/route-test" element={<RouteTestPage />} />
            <Route path="/logo-preview" element={<LogoPreview />} />
            
            {/* Legal Pages */}
            <Route path="/legal/:page" element={<LegalPages />} />
            <Route path="/privacy" element={<LegalPages />} />
            <Route path="/terms" element={<LegalPages />} />
            <Route path="/hipaa" element={<LegalPages />} />
            
            {/* Admin Routes */}
            <Route path="/test" element={<TestDashboard />} />
            <Route path="/minimal" element={<MinimalDashboard />} />
            
            {/* Command Center - Main admin dashboard */}
            <Route path="/command" element={<RevolutionaryAdminDashboard />} />
            <Route path="/command/*" element={<RevolutionaryAdminDashboard />} />
            
            {/* Alternative routes */}
            <Route path="/admin-revolutionary" element={<RevolutionaryAdminDashboard />} />
            <Route path="/admin-revolutionary/*" element={<RevolutionaryAdminDashboard />} />
            
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/*" element={<AdminDashboard />} />
            <Route path="/dashboard" element={<App />} />
            <Route path="/dashboard/*" element={<App />} />
            
            {/* Default Route - Native redirect or Professional Landing Page */}
            <Route path="/" element={getNativeRedirect() ? <Navigate to={getNativeRedirect()} replace /> : <ProfessionalLandingPage />} />
            
            {/* Catch-all Route - Show Professional Landing Page */}
            <Route path="*" element={<ProfessionalLandingPage />} />
          </Routes>
        </Suspense>
      </TenantProvider>
    </BrowserRouter>
    </AppErrorBoundary>
  );
}

export default AppRouter;
